import { Tile } from "../Tile";
import InsuranceId from "../../svg/insurance-id.svg";
import ManagePolicy from "../../svg/manage-policy.svg";
import Documents from "../../svg/documents.svg";
import FileClaim from "../../svg/file-claim.svg";
import { useUrls } from "../../app/Providers/AppContext";
import { getStateName } from "shared";
import { getVehiclesLabel } from "../../utils/vehicleUtils";
import styles from "./InsuranceTiles.module.scss";
import cx from "clsx";
import { TagAction } from "../../types/TagAction.types";
import { useDialog } from "../Dialog";
import { logModal } from "../../utils/logUserActions";
import { CompOnlyDialog } from "./CompOnlyDialog";
import { Policy } from "@hagerty/self-service-components/types/providerResponses/shared";

type InsuranceTilesProps = {
  policy: Policy;
};

const action = "insurance_button_clicked";
const idTagAction: TagAction = { name: "id", action };
const policyDocumentsTagAction: TagAction = {
  name: "policy_documents",
  action,
};
const managePolicyTagAction: TagAction = { name: "manage_policy", action };

export const InsuranceTiles = ({ policy }: InsuranceTilesProps) => {
  const urls = useUrls();
  const vehicleNamesLabel = getVehiclesLabel(policy.vehicles);
  const { close, open, isOpen } = useDialog();
  const { policyNumber, partner, country, state, hasIdCard } = policy;
  const stateName = getStateName(state);
  const claimsCta = country === "USA" ? "Manage claims" : "File a claim";
  const claimsTagAction: TagAction = {
    name: country === "USA" ? "manage_claims" : "file_claim",
    action,
  };
  const claimsUrl = urls.insurance.fileClaim(policyNumber, country);
  const shouldRenderManagePolicy = partner !== "state-farm";

  const insuranceIdAction = hasIdCard
    ? {
        href: urls.insurance.getCard(window.location.origin, policyNumber),
        newTab: true,
        tagAction: idTagAction,
      }
    : {
        onClick: () => {
          logModal("comp only");
          open();
        },
      };

  const managePolicyUrl = urls.insurance.updatePolicy(policyNumber, country);

  return (
    <>
      <div className={cx(styles.row, styles.rowWrapper)}>
        <div className={styles.row}>
          <Tile
            icon={
              <img src={InsuranceId} alt="Insurance ID" aria-hidden="true" />
            }
            {...insuranceIdAction}
            className={styles.tileSpacer}
            cta="Insurance ID"
            ariaLabel={`Access Insurance ID for your ${vehicleNamesLabel} on policy ${policyNumber}`}
          />
          <Tile
            icon={<img src={FileClaim} alt={claimsCta} aria-hidden="true" />}
            className={styles.fullScreenTileSpacer}
            cta={claimsCta}
            ariaLabel={`${claimsCta} for your ${vehicleNamesLabel} on policy ${policyNumber}`}
            href={claimsUrl}
            tagAction={claimsTagAction}
          />
        </div>

        <div className={cx(styles.row, styles.secondRow)}>
          <Tile
            icon={
              <img src={Documents} alt="Policy documents" aria-hidden="true" />
            }
            className={styles.tileSpacer}
            cta="Policy documents"
            ariaLabel={`Policy documents for policy ${policyNumber}`}
            href={urls.insurance.viewPolicy(policyNumber, country)}
            tagAction={policyDocumentsTagAction}
          />
          {shouldRenderManagePolicy ? (
            <Tile
              icon={
                <img
                  src={ManagePolicy}
                  alt="Manage policy"
                  aria-hidden="true"
                />
              }
              className={styles.fullScreenTileSpacer}
              cta="Manage policy"
              ariaLabel={`Manage policy ${policyNumber} for your ${vehicleNamesLabel}`}
              href={managePolicyUrl}
              tagAction={managePolicyTagAction}
            />
          ) : (
            <div className={styles.tilePlaceholder} />
          )}
        </div>
      </div>
      {isOpen && (
        <CompOnlyDialog
          stateName={stateName}
          managePolicyUrl={managePolicyUrl}
          onClose={close}
        />
      )}
    </>
  );
};
